import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import {
  INN_ERROR,
  KPP_ERROR,
  MAX_CHARACTERS_ERROR,
  MIN_CHARACTERS_ERROR,
  OGRN_LENGTH_ERROR,
  RAFP_NZA_ERROR,
  REQUIRED_ERROR,
  UNALLOWED_CHARACTERS_ERROR,
} from 'CorePackage/MainModule/constants/validationErrors';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeIsOgrn } from 'CorePackage/MainModule/validators/isOgrn';
import { makeIsErknmInn } from 'CorePackage/MainModule/validators/makeIsErknmInn';
import { makeInnChecksumVerification } from 'CorePackage/MainModule/validators/isInnCorrect';
import { isDigitsCountCorrect } from 'CorePackage/MainModule/validators/isDigitsCountCorrect';
import { validateRafpNzaStatus } from 'CorePackage/MainModule/validators/validateRafpNzaStatus';
import { makeSnilsValidation } from 'CorePackage/MainModule/validators/isValidSnils';
import { makeMinCharactersValidator } from 'CorePackage/MainModule/validators/minCharacters';

import { IErknmFormAgreementPetitionOrganization } from '../interfaces/IErknmFormAgreementPetitionOrganization';

export const getAgreementPetitionOrganizationValidate = (
  petitionOrganization: IErknmFormAgreementPetitionOrganization | undefined,
) => {
  if (!petitionOrganization) {
    return undefined;
  }

  const isFIZ = petitionOrganization.subjectType?.label === KnmSubjectTypesEnum.FIZ;
  const isFIUR = petitionOrganization.subjectType?.label === KnmSubjectTypesEnum.FIUR;
  const isIUR = petitionOrganization.subjectType?.label === KnmSubjectTypesEnum.IUR;
  const isIFIZ = petitionOrganization.subjectType?.label === KnmSubjectTypesEnum.IFIZ;
  const isUR = petitionOrganization.subjectType?.label === KnmSubjectTypesEnum.UR;
  const isIP = petitionOrganization.subjectType?.label === KnmSubjectTypesEnum.IP;

  return {
    subjectType: composeValidators(makeRequired(REQUIRED_ERROR))(petitionOrganization.subjectType),
    ogrn:
      petitionOrganization.subjectType && (isIP || isUR)
        ? composeValidators(
            makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
            makeMaxCharactersValidator(32, MAX_CHARACTERS_ERROR),
            makeIsOgrn(OGRN_LENGTH_ERROR, petitionOrganization.subjectType?.label),
            makeRequired(REQUIRED_ERROR),
          )(petitionOrganization.ogrn)
        : undefined,
    inn: composeValidators(
      !petitionOrganization.rafpNza && !isIUR && !isIFIZ && !petitionOrganization.snils
        ? makeRequired(REQUIRED_ERROR)
        : () => undefined,
      petitionOrganization.inn ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      petitionOrganization.inn
        ? makeIsErknmInn({
            errorMessage: INN_ERROR(),
            getIsFiz: () => isFIZ,
            getIsRequired: () => true,
          })
        : () => undefined,
      petitionOrganization.inn ? makeInnChecksumVerification() : () => undefined,
    )(petitionOrganization.inn),
    rafpNza: composeValidators(
      isFIUR && !petitionOrganization.inn && !petitionOrganization.kpp ? makeRequired(REQUIRED_ERROR) : () => undefined,
      isFIUR && !petitionOrganization.inn && !petitionOrganization.kpp
        ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR)
        : () => undefined,
      isFIUR && !petitionOrganization.inn && !petitionOrganization.kpp
        ? isDigitsCountCorrect({
            errorMessage: RAFP_NZA_ERROR,
            digitsCount: 11,
          })
        : () => undefined,
      isFIUR && !petitionOrganization.inn && !petitionOrganization.kpp ? validateRafpNzaStatus() : () => undefined,
    )(petitionOrganization.rafpNza),
    kpp: composeValidators(
      isFIUR && !petitionOrganization.rafpNza ? makeRequired(REQUIRED_ERROR) : () => undefined,
      isFIUR && !petitionOrganization.rafpNza ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      isFIUR && !petitionOrganization.rafpNza
        ? isDigitsCountCorrect({
            errorMessage: KPP_ERROR,
            digitsCount: 9,
          })
        : () => undefined,
    )(petitionOrganization.kpp),
    organizationName: composeValidators(
      isIFIZ || isFIZ || isIUR ? makeRequired(REQUIRED_ERROR) : () => undefined,
      makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
    )(petitionOrganization.organizationName),
    snils: composeValidators(
      !petitionOrganization.inn && isFIZ ? makeRequired(REQUIRED_ERROR) : () => undefined,
      makeSnilsValidation('Поле заполнено некорректно', () => !petitionOrganization.snils),
    )(petitionOrganization.snils),
    country: makeRequired(REQUIRED_ERROR, () => !isIUR && !isIFIZ)(petitionOrganization.country),
    document: composeValidators(
      makeRequired(REQUIRED_ERROR, () => !isIFIZ),
      makeMinCharactersValidator(3, MIN_CHARACTERS_ERROR(3), () => !isIFIZ),
      makeMaxCharactersValidator(1000, MAX_CHARACTERS_ERROR, () => !isIFIZ),
    )(petitionOrganization.document),
  };
};
